// src/pinball/PinballPhysics.ts - Physics engine with gravity, Verlet integration, flipper rotation

import {
  PhysicsBody,
  FlipperBody,
  StaticCollider,
  CollisionEvent,
  PinballPhysicsConfig,
  DEFAULT_PINBALL_PHYSICS,
  Vector2D,
  CircleShape,
  SegmentShape,
  ArcShape,
  CollisionResult,
} from './types';
import { CollisionDetector } from './CollisionDetector';

export class PinballPhysics {
  private config: PinballPhysicsConfig;
  private bodies: Map<string, PhysicsBody>;
  private flippers: Map<string, FlipperBody>;
  private statics: StaticCollider[];
  private collisionDetector: CollisionDetector;
  private collisionCooldowns: Map<string, number>;

  constructor(config: Partial<PinballPhysicsConfig> = {}) {
    this.config = { ...DEFAULT_PINBALL_PHYSICS, ...config };
    this.bodies = new Map();
    this.flippers = new Map();
    this.statics = [];
    this.collisionDetector = new CollisionDetector(this.config.spatialGridCellSize);
    this.collisionCooldowns = new Map();
  }

  // Body management
  addBody(body: PhysicsBody): void {
    this.bodies.set(body.id, body);
  }

  removeBody(id: string): void {
    this.bodies.delete(id);
  }

  getBody(id: string): PhysicsBody | undefined {
    return this.bodies.get(id);
  }

  getBodies(): PhysicsBody[] {
    return Array.from(this.bodies.values());
  }

  // Flipper management
  addFlipper(flipper: FlipperBody): void {
    this.flippers.set(flipper.id, flipper);
  }

  setFlipperState(id: string, active: boolean): void {
    const flipper = this.flippers.get(id);
    if (flipper) {
      flipper.isActive = active;
    }
  }

  getFlippers(): FlipperBody[] {
    return Array.from(this.flippers.values());
  }

  // Static colliders
  setStaticColliders(colliders: StaticCollider[]): void {
    this.statics = colliders;
  }

  // Main simulation step
  step(dt: number): CollisionEvent[] {
    const events: CollisionEvent[] = [];
    const substepDt = dt / this.config.physicsSubsteps;

    for (let i = 0; i < this.config.physicsSubsteps; i++) {
      this.substep(substepDt, events);
    }

    return events;
  }

  private substep(dt: number, events: CollisionEvent[]): void {
    // Update flippers
    this.updateFlippers(dt);

    // Apply gravity and integrate
    for (const body of this.bodies.values()) {
      if (!body.isActive) continue;

      // Apply gravity
      body.velocity.y += this.config.gravity * dt;

      // Apply rolling friction
      body.velocity.x *= this.config.rollingFriction;
      body.velocity.y *= this.config.rollingFriction;

      // Cap velocity
      const speed = Math.sqrt(body.velocity.x ** 2 + body.velocity.y ** 2);
      if (speed > this.config.maxBallSpeed) {
        const scale = this.config.maxBallSpeed / speed;
        body.velocity.x *= scale;
        body.velocity.y *= scale;
      }

      // Integrate position (semi-implicit Euler)
      body.position.x += body.velocity.x * dt;
      body.position.y += body.velocity.y * dt;
    }

    // Collision detection
    const activeBodies = Array.from(this.bodies.values()).filter(b => b.isActive);
    this.collisionDetector.updateSpatialGrid(activeBodies, this.statics);
    const pairs = this.collisionDetector.getPotentialPairs();

    // Resolve collisions
    for (const pair of pairs) {
      const body = this.bodies.get(pair.bodyId);
      if (!body || !body.isActive) continue;

      // Check if it's a body-body collision
      const otherBody = this.bodies.get(pair.colliderId);
      if (otherBody && otherBody.isActive) {
        const result = this.collisionDetector.testCircleCircle(body, otherBody);
        if (result) {
          this.resolveBodyBodyCollision(body, otherBody, result);
          events.push({
            bodyId: body.id,
            elementId: otherBody.id,
            elementType: 'wall',
            contactPoint: result.contactPoint,
            contactNormal: result.normal,
            relativeSpeed: Math.sqrt(
              (body.velocity.x - otherBody.velocity.x) ** 2 +
              (body.velocity.y - otherBody.velocity.y) ** 2
            ),
            timestamp: Date.now(),
          });
        }
        continue;
      }

      // Body vs static collider
      const collider = this.statics.find(c => c.id === pair.colliderId);
      if (!collider) continue;

      // Skip if on cooldown (prevents rapid re-collision with same element)
      const cooldownKey = `${body.id}_${collider.id}`;
      const cooldownExpiry = this.collisionCooldowns.get(cooldownKey);
      if (cooldownExpiry && Date.now() < cooldownExpiry) continue;

      const result = this.testBodyCollider(body, collider, dt);
      if (result) {
        const restitution = collider.restitution || this.config.defaultRestitution;
        this.resolveCollision(body, result, restitution);

        // Set cooldown (50ms) to prevent immediate re-collision
        this.collisionCooldowns.set(cooldownKey, Date.now() + 50);

        events.push({
          bodyId: body.id,
          elementId: collider.elementRef,
          elementType: this.getElementTypeFromCollider(collider),
          contactPoint: result.contactPoint,
          contactNormal: result.normal,
          relativeSpeed: Math.sqrt(body.velocity.x ** 2 + body.velocity.y ** 2),
          timestamp: Date.now(),
        });
      }
    }

    // Flipper collisions
    for (const flipper of this.flippers.values()) {
      for (const body of this.bodies.values()) {
        if (!body.isActive) continue;

        const result = this.collisionDetector.testSweptCircleFlipper(body, flipper, dt);
        if (result) {
          this.resolveFlipperCollision(body, flipper, result);
          events.push({
            bodyId: body.id,
            elementId: flipper.id,
            elementType: 'flipper',
            contactPoint: result.contactPoint,
            contactNormal: result.normal,
            relativeSpeed: Math.sqrt(body.velocity.x ** 2 + body.velocity.y ** 2),
            timestamp: Date.now(),
          });
        }
      }
    }
  }

  private updateFlippers(dt: number): void {
    for (const flipper of this.flippers.values()) {
      const targetAngle = flipper.isActive ? flipper.activeAngle : flipper.restAngle;
      const diff = targetAngle - flipper.currentAngle;

      if (Math.abs(diff) < 0.01) {
        flipper.currentAngle = targetAngle;
        flipper.angularVelocity = 0;
        continue;
      }

      // Determine speed based on activation/return
      const speed = flipper.isActive
        ? flipper.maxAngularVelocity
        : flipper.maxAngularVelocity * 0.6;

      flipper.angularVelocity = Math.sign(diff) * speed;
      flipper.currentAngle += flipper.angularVelocity * dt;

      // Clamp to target
      if (flipper.side === 'left') {
        if (flipper.isActive) {
          flipper.currentAngle = Math.min(flipper.currentAngle, flipper.activeAngle);
        } else {
          flipper.currentAngle = Math.max(flipper.currentAngle, flipper.restAngle);
        }
      } else {
        if (flipper.isActive) {
          flipper.currentAngle = Math.max(flipper.currentAngle, flipper.activeAngle);
        } else {
          flipper.currentAngle = Math.min(flipper.currentAngle, flipper.restAngle);
        }
      }
    }
  }

  private testBodyCollider(body: PhysicsBody, collider: StaticCollider, dt: number): CollisionResult | null {
    switch (collider.type) {
      case 'circle': {
        const shape = collider.shape as CircleShape;
        const fakeBody: PhysicsBody = {
          id: collider.id,
          position: shape.center,
          velocity: { x: 0, y: 0 },
          radius: shape.radius,
          mass: Infinity,
          isActive: true,
        };
        return this.collisionDetector.testCircleCircle(body, fakeBody);
      }
      case 'segment': {
        const shape = collider.shape as SegmentShape;
        return this.collisionDetector.testSweptCircleSegment(body, shape, dt);
      }
      case 'arc': {
        const shape = collider.shape as ArcShape;
        return this.collisionDetector.testCircleArc(body, shape);
      }
      default:
        return null;
    }
  }

  private resolveCollision(body: PhysicsBody, result: CollisionResult, restitution: number): void {
    // Separate body from collision
    body.position.x += result.normal.x * (result.penetration + 1);
    body.position.y += result.normal.y * (result.penetration + 1);

    // Calculate incoming speed along normal
    const incomingSpeed = Math.abs(body.velocity.x * result.normal.x + body.velocity.y * result.normal.y);

    // If ball is hitting at very low speed, don't bounce — just slide
    if (incomingSpeed < 100) {
      // Remove normal component entirely (no bounce), keep tangential (sliding)
      const normalVel = body.velocity.x * result.normal.x + body.velocity.y * result.normal.y;
      body.velocity.x -= normalVel * result.normal.x;
      body.velocity.y -= normalVel * result.normal.y;
      // Apply strong friction to stop sliding quickly
      body.velocity.x *= 0.8;
      body.velocity.y *= 0.8;
      // If very slow, just stop
      const speed = Math.sqrt(body.velocity.x ** 2 + body.velocity.y ** 2);
      if (speed < 20) {
        body.velocity.x = 0;
        body.velocity.y = 0;
      }
    } else {
      // Normal bounce with restitution
      const dot = body.velocity.x * result.normal.x + body.velocity.y * result.normal.y;
      body.velocity.x -= (1 + restitution) * dot * result.normal.x;
      body.velocity.y -= (1 + restitution) * dot * result.normal.y;
    }
  }

  private resolveBodyBodyCollision(a: PhysicsBody, b: PhysicsBody, result: CollisionResult): void {
    // Separate bodies
    const totalMass = a.mass + b.mass;
    const aRatio = b.mass / totalMass;
    const bRatio = a.mass / totalMass;

    a.position.x -= result.normal.x * result.penetration * aRatio;
    a.position.y -= result.normal.y * result.penetration * aRatio;
    b.position.x += result.normal.x * result.penetration * bRatio;
    b.position.y += result.normal.y * result.penetration * bRatio;

    // Elastic collision response
    const relVelX = a.velocity.x - b.velocity.x;
    const relVelY = a.velocity.y - b.velocity.y;
    const relVelDotNormal = relVelX * result.normal.x + relVelY * result.normal.y;

    if (relVelDotNormal > 0) return; // Moving apart

    const restitution = this.config.defaultRestitution;
    const impulse = -(1 + restitution) * relVelDotNormal / totalMass;

    a.velocity.x += impulse * b.mass * result.normal.x;
    a.velocity.y += impulse * b.mass * result.normal.y;
    b.velocity.x -= impulse * a.mass * result.normal.x;
    b.velocity.y -= impulse * a.mass * result.normal.y;
  }

  private resolveFlipperCollision(body: PhysicsBody, flipper: FlipperBody, result: CollisionResult): void {
    // Separate body
    body.position.x += result.normal.x * result.penetration;
    body.position.y += result.normal.y * result.penetration;

    // Calculate flipper velocity at contact point
    const contactDist = Math.sqrt(
      (result.contactPoint.x - flipper.pivot.x) ** 2 +
      (result.contactPoint.y - flipper.pivot.y) ** 2
    );

    // Tangential velocity from rotation
    const tangentX = -(result.contactPoint.y - flipper.pivot.y) * flipper.angularVelocity;
    const tangentY = (result.contactPoint.x - flipper.pivot.x) * flipper.angularVelocity;

    // Relative velocity
    const relVelX = body.velocity.x - tangentX;
    const relVelY = body.velocity.y - tangentY;
    const relVelDotNormal = relVelX * result.normal.x + relVelY * result.normal.y;

    if (relVelDotNormal > 0) return; // Moving apart

    // Apply impulse with momentum transfer
    const restitution = 0.85;
    const impulse = -(1 + restitution) * relVelDotNormal;

    body.velocity.x += impulse * result.normal.x;
    body.velocity.y += impulse * result.normal.y;

    // Add flipper's tangential velocity contribution (momentum transfer)
    const momentumFactor = Math.min(contactDist / flipper.length, 1.0) * 0.5;
    body.velocity.x += tangentX * momentumFactor;
    body.velocity.y += tangentY * momentumFactor;
  }

  private getElementTypeFromCollider(collider: StaticCollider): 'wall' | 'flipper' | 'drain' {
    if (collider.elementRef.includes('drain')) return 'drain';
    if (collider.elementRef.includes('flipper')) return 'flipper';
    return 'wall';
  }

  // Configuration
  updateConfig(partial: Partial<PinballPhysicsConfig>): void {
    this.config = { ...this.config, ...partial };
    this.collisionDetector = new CollisionDetector(this.config.spatialGridCellSize);
  }

  getConfig(): PinballPhysicsConfig {
    return { ...this.config };
  }
}
